import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Hammer, MapPin, CheckCircle, Circle, ArrowsClockwise, Flag, Camera,
  Briefcase, ShieldCheck, Heart, ChatCircle, PaperPlaneTilt,
} from '@phosphor-icons/react';
import { Lightbox } from '../components/Lightbox';
import { LOGO_URL } from '../config/branding';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getViewerId = () => {
  let v = localStorage.getItem('ch_viewer_id');
  if (!v) {
    v = `v-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    localStorage.setItem('ch_viewer_id', v);
  }
  return v;
};

const getGuestName = () => localStorage.getItem('ch_guest_name') || '';

const STATUS = {
  planning: { bg: 'bg-blue-500', label: 'Planning' },
  in_progress: { bg: 'bg-amber-500', label: 'In Progress' },
  completed: { bg: 'bg-emerald-500', label: 'Completed' },
  on_hold: { bg: 'bg-neutral-500', label: 'On Hold' },
};

const MS = {
  pending: { icon: Circle, color: 'text-neutral-400' },
  in_progress: { icon: ArrowsClockwise, color: 'text-amber-500' },
  completed: { icon: CheckCircle, color: 'text-emerald-500' },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

export default function PublicProgress() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [guestName, setGuestName] = useState(getGuestName());
  const [openThread, setOpenThread] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/project-tracker/public/${token}?viewer_id=${encodeURIComponent(getViewerId())}`);
      if (!res.ok) {
        setError('This progress link is no longer available.');
        return;
      }
      setData(await res.json());
    } catch {
      setError('Could not load project progress.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleLove = async (updateId) => {
    setData((prev) => prev && ({
      ...prev,
      updates: prev.updates.map((u) => u.id === updateId
        ? { ...u, loved: !u.loved, loved_count: (u.loved_count || 0) + (u.loved ? -1 : 1) }
        : u),
    }));
    try {
      const res = await fetch(`${API_URL}/api/project-tracker/public/${token}/updates/${updateId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ viewer_id: getViewerId() }),
      });
      const r = await res.json();
      if (r.success) {
        setData((prev) => prev && ({
          ...prev,
          updates: prev.updates.map((u) => u.id === updateId
            ? { ...u, loved: r.loved, loved_count: r.loved_count } : u),
        }));
      }
    } catch {
      setData((prev) => prev && ({
        ...prev,
        updates: prev.updates.map((u) => u.id === updateId
          ? { ...u, loved: !u.loved, loved_count: (u.loved_count || 0) + (u.loved ? -1 : 1) }
          : u),
      }));
    }
  };

  const submitComment = async (updateId) => {
    const text = (commentInputs[updateId] || '').trim();
    if (!text) return;
    const name = (guestName || '').trim();
    if (!name) {
      toast({ title: 'Add your name first', description: 'Enter your name so the contractor knows who commented.' });
      return;
    }
    localStorage.setItem('ch_guest_name', name);
    setCommentInputs((p) => ({ ...p, [updateId]: '' }));
    try {
      const res = await fetch(`${API_URL}/api/project-tracker/public/${token}/updates/${updateId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ viewer_id: getViewerId(), author_name: name, text }),
      });
      const r = await res.json();
      if (r.success) {
        setData((prev) => prev && ({
          ...prev,
          updates: prev.updates.map((u) => u.id === updateId
            ? { ...u, comments: [...(u.comments || []), r.comment] } : u),
        }));
      }
    } catch {
      setCommentInputs((p) => ({ ...p, [updateId]: text }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E8E9EB]">
        <ArrowsClockwise size={28} className="animate-spin text-[#D95800]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#E8E9EB] px-6 text-center" data-testid="public-progress-error">
        <div className="w-16 h-16 bg-black border-4 border-[#D95800] flex items-center justify-center mb-4">
          <Hammer size={28} weight="fill" className="text-[#D95800]" />
        </div>
        <h1 className="text-xl font-black uppercase text-neutral-900">Link unavailable</h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-sm">{error || 'This progress link is no longer available.'}</p>
      </div>
    );
  }

  const { project, milestones, updates } = data;
  const st = STATUS[project.status] || STATUS.planning;
  const photos = updates.filter((u) => u.photo_url).map((u) => u.photo_url);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="public-progress-page">
      {/* Header */}
      <header className="bg-black text-white border-b-4 border-[#D95800]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="ConstructHelp" className="h-8" />
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-[#D95800] text-black px-2 py-1">
            <ShieldCheck size={12} weight="fill" /> Read-only
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Project header */}
        <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[6px_6px_0px_#D95800] p-5 mb-6" data-testid="public-project-header">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h1 className="text-2xl font-black uppercase tracking-tight text-neutral-900 dark:text-white">{project.title}</h1>
            <span className={`px-2.5 py-1 ${st.bg} text-white text-xs font-black uppercase whitespace-nowrap`}>{st.label}</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400 mb-4">
            {project.contractor_name && (
              <span className="flex items-center gap-1"><Briefcase size={14} weight="bold" /> {project.contractor_name}</span>
            )}
            {project.location && (
              <span className="flex items-center gap-1"><MapPin size={14} weight="bold" /> {project.location}</span>
            )}
            {project.started_at && <span>Started {fmtDate(project.started_at)}</span>}
          </div>
          <div className="w-full h-3 bg-neutral-200 dark:bg-neutral-700 border-2 border-black overflow-hidden">
            <div className={`h-full ${project.progress === 100 ? 'bg-emerald-500' : 'bg-[#D95800]'} transition-all`} style={{ width: `${project.progress}%` }} />
          </div>
          <div className="flex items-center justify-between text-xs font-bold text-neutral-500 mt-1.5">
            <span>{project.progress}% complete</span>
            <span>{project.completed_milestones}/{project.total_milestones} milestones</span>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          {/* Milestones */}
          <div className="md:col-span-2">
            <h2 className="text-sm font-black uppercase text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
              <Flag size={18} weight="bold" className="text-[#2B4C6F]" /> Milestones
            </h2>
            {milestones.length === 0 ? (
              <p className="text-sm text-neutral-500">No milestones yet.</p>
            ) : (
              <div className="space-y-2" data-testid="public-milestones">
                {milestones.map((m, i) => {
                  const cfg = MS[m.status] || MS.pending;
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className="bg-white dark:bg-[#0F172A] border-2 border-black p-3 flex items-start gap-2">
                      <Icon size={18} weight={m.status === 'completed' ? 'fill' : 'bold'} className={`${cfg.color} flex-shrink-0 mt-0.5`} />
                      <div className="min-w-0">
                        <p className={`font-bold text-sm ${m.status === 'completed' ? 'text-neutral-400 line-through' : 'text-neutral-900 dark:text-white'}`}>{m.title}</p>
                        {m.description && <p className="text-xs text-neutral-500 mt-0.5">{m.description}</p>}
                        {m.due_date && <p className="text-[11px] text-neutral-400 mt-0.5">Due {fmtDate(m.due_date)}</p>}
                        {m.approved && (
                          <span
                            className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-wide border-2 border-black"
                            data-testid={`public-milestone-approved-${i}`}
                          >
                            <CheckCircle size={11} weight="fill" /> Approved by {m.approved_by_name}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="md:col-span-3">
            <h2 className="text-sm font-black uppercase text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
              <Camera size={18} weight="bold" className="text-[#D95800]" /> Progress Updates
            </h2>
            {updates.length === 0 ? (
              <div className="bg-white dark:bg-[#0F172A] border-2 border-dashed border-neutral-300 dark:border-neutral-700 p-8 text-center">
                <p className="text-sm text-neutral-500 font-bold">No updates posted yet</p>
                <p className="text-xs text-neutral-400 mt-1">Check back soon — photos and progress notes will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="public-updates">
                {updates.map((u, i) => (
                  <div key={i} className="bg-white dark:bg-[#0F172A] border-2 border-black p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-black uppercase tracking-wide text-[#D95800]">{u.author_name || 'Update'}</span>
                      <span className="text-[11px] text-neutral-400">{fmtDate(u.created_at)}</span>
                    </div>
                    {u.message && <p className="text-sm text-neutral-800 dark:text-neutral-200 leading-relaxed">{u.message}</p>}
                    {u.photo_url && (
                      <button
                        onClick={() => setLightbox(photos.indexOf(u.photo_url))}
                        className="mt-2 block w-full border-2 border-black overflow-hidden"
                        data-testid={`public-update-photo-${i}`}
                      >
                        <img src={u.photo_url} alt="Progress" className="w-full max-h-72 object-cover hover:opacity-90 transition-opacity" />
                      </button>
                    )}
                    {!u.is_system && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => toggleLove(u.id)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-black text-xs font-black uppercase tracking-wide transition-all active:translate-y-0.5 ${
                            u.loved ? 'bg-[#D95800] text-white shadow-[2px_2px_0px_#000]' : 'bg-white dark:bg-neutral-800 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                          }`}
                          data-testid={`public-love-btn-${i}`}
                          title={u.loved ? 'Remove love' : 'Love this update'}
                        >
                          <Heart size={14} weight={u.loved ? 'fill' : 'bold'} />
                          {u.loved ? 'Loved' : 'Love this'}
                          {u.loved_count > 0 && <span className="ml-0.5">· {u.loved_count}</span>}
                        </button>
                        <button
                          onClick={() => setOpenThread(openThread === u.id ? null : u.id)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-black text-xs font-black uppercase tracking-wide bg-white dark:bg-neutral-800 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all active:translate-y-0.5"
                          data-testid={`public-comment-btn-${i}`}
                        >
                          <ChatCircle size={14} weight="bold" /> Comment
                          {(u.comments?.length || 0) > 0 && <span className="ml-0.5">· {u.comments.length}</span>}
                        </button>
                      </div>
                    )}
                    {!u.is_system && (openThread === u.id || (u.comments?.length || 0) > 0) && (
                      <div className="mt-2 border-t-2 border-neutral-200 dark:border-neutral-700 pt-2 space-y-2" data-testid={`public-thread-${i}`}>
                        {(u.comments || []).map((c) => (
                          <div key={c.id} className="text-sm">
                            <span className={`font-black ${c.author_type === 'contractor' ? 'text-[#2B4C6F]' : c.author_type === 'homeowner' ? 'text-[#D95800]' : 'text-neutral-700 dark:text-neutral-300'}`}>{c.author_name}</span>
                            <span className="text-neutral-700 dark:text-neutral-300"> {c.text}</span>
                          </div>
                        ))}
                        {openThread === u.id && (
                          <div className="space-y-2 pt-1">
                            {!getGuestName() && (
                              <input
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                placeholder="Your name"
                                className="w-full h-9 px-2 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D95800]"
                                data-testid={`public-guest-name-${i}`}
                              />
                            )}
                            <div className="flex items-center gap-2">
                              <input
                                value={commentInputs[u.id] || ''}
                                onChange={(e) => setCommentInputs((p) => ({ ...p, [u.id]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') submitComment(u.id); }}
                                placeholder="Add a comment…"
                                className="flex-1 h-9 px-2 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D95800]"
                                data-testid={`public-comment-input-${i}`}
                              />
                              <button
                                onClick={() => submitComment(u.id)}
                                className="h-9 px-3 bg-[#D95800] hover:bg-[#B84A00] text-white border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none flex items-center"
                                data-testid={`public-comment-send-${i}`}
                              >
                                <PaperPlaneTilt size={14} weight="bold" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-neutral-400 mt-8">
          Powered by <span className="font-bold text-neutral-500">ConstructHelp</span> · Live project progress
        </p>
      </main>

      {lightbox !== null && photos.length > 0 && (
        <Lightbox images={photos} initialIndex={lightbox} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}
